import { useContext, useState, useRef, useEffect } from "react";
import { FiChevronDown, FiUser, FiSearch, FiX } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import Context from "../context/Context";
import { CiLogout } from "react-icons/ci";
import useDebounce from "../hooks/useDebounce";
import { searchGlobal } from "../api/searchApi";
import SearchResults from "./search/SearchResults";

export default function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user, setUser, pageTitle, isSidebarOpen } = useContext(Context);

  const profileRef = useRef(null);
  const searchRef = useRef(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const onLogout = () => {
    Cookies.remove("authToken");
    setUser(null);
    navigate("/login");
  };

  const fullName = `${user?.first_name?.charAt(0).toUpperCase() + user?.first_name?.slice(1)} ${user?.last_name?.charAt(0).toUpperCase() + user?.last_name?.slice(1)}`;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (debouncedSearchQuery) {
      setIsLoading(true);
      searchGlobal(debouncedSearchQuery).then((results) => {
        setSearchResults(results);
        setIsLoading(false);
      });
    } else {
      setSearchResults(null);
    }
  }, [debouncedSearchQuery]);

  // Close mobile search when screen size is back to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) { // 768px is md breakpoint in tailwind
        setMobileSearchOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  return (
    <>
      <header className={`sticky top-0 z-30 flex items-center justify-between px-4 py-2 bg-white shadow-sm border-b header-body ${isSidebarOpen ? '!w-[calc(100%-250px)]' : '!w-full'}`}>
        <h2 className="text-2xl font-semibold truncate !mb-0" style={{ paddingLeft: isSidebarOpen ? '170px' : '0px', transition: 'padding-left 0.3s ease' }}>{pageTitle}</h2>

        {/* Desktop Search Bar */}
        <div className="hidden md:flex flex-1 justify-center px-4">
          <div className="relative w-full max-w-md" ref={searchRef}>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search..."
              className="block w-full bg-gray-100 border border-gray-200 rounded-sm py-2 pl-10 pr-3 text-sm placeholder-gray-500 focus:outline-none focus:bg-white focus:border-blue-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
            />
            {isSearchFocused && (searchQuery || isLoading) && (
              <div className="absolute mt-1 w-full rounded-lg shadow-lg bg-white border border-gray-200 z-50 max-h-96 overflow-y-auto">
                <SearchResults
                  isLoading={isLoading}
                  results={searchResults}
                  query={debouncedSearchQuery}
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Mobile Search Button */}
          <button onClick={() => setMobileSearchOpen(true)} className="md:hidden p-2 !rounded-[5px] hover:bg-gray-100">
            <FiSearch className="h-5 w-5 text-gray-500" />
          </button>

          <div className="relative" ref={profileRef}>
            <button
              onClick={() => setOpen(!open)}
              className="flex items-center gap-1.5 p-1 rounded-[5px] hover:bg-gray-100 transition-colors"
            >
              <div className="h-10 w-10 flex-shrink-0 rounded-full bg-[#E5E7EB] overflow-hidden">
                {user?.profile_image ? (
                  <img src={user.profile_image} alt="Profile" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-xs font-bold text-gray-600 bg-gray-200">
                    {`${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase()}
                  </div>
                )}
              </div>
              <FiChevronDown className="h-3.5 w-3.5 text-gray-700" />
            </button>

            {open && (
              <div className="absolute right-0 mt-2.5 w-[260px] bg-white shadow-lg rounded-[5px]  z-50 overflow-hidden">
                {/* Header Section: Compact Alignment */}
                <div className="flex items-center gap-2 px-4 py-3">
                  <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-200 overflow-hidden">
                    {user?.profile_image ? (
                      <img src={user.profile_image} alt="Profile" className="h-full w-full object-cover" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-xs font-bold text-gray-500">
                        {`${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 min-w-0 leading-tight">
                    <h4 className="text-[16px] text-[#000000] !mb-0 font-medium truncate">
                      {fullName}
                    </h4>
                    <p className="text-[12px] font-normal !mb-1 text-[#828282] truncate">
                      {user?.email}
                    </p>
                    <span className="inline-flex items-center px-2 py-1 rounded text-[10px] font-medium bg-blue-50 text-black-700 border border-black-100 uppercase tracking-wide">
                      {user.role}
                    </span>
                  </div>
                </div>



                {/* Menu Options */}
                <div className="pb-1 ">
                  <Link to="/profile" onClick={() => setOpen(false)} className="block no-underline">
                    <button className="w-full flex items-center gap-3 px-4 py-2.5 text-[16px] font-normal text-[#000000] hover:bg-gray-50 transition-colors text-left">
                      <FiUser className="h-5 w-5 text-[#000000]" />
                      <span>Profile</span>
                    </button>
                  </Link>

                  <div className="h-[1px] border-1 bg-[#E5E5E5] my-1 mx-2" />

                  <button
                    onClick={onLogout}
                    className="w-full text-[#b91c1c] flex items-center gap-3 px-4 py-2.5 text-[16px] font-normal transition-colors text-left"
                  >
                    <CiLogout color={'#b91c1c'} className="h-5 w-5 " />
                    <span>Log out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Search Overlay */}
      {mobileSearchOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-white">
          <div className="relative w-full h-full">
            <div className="flex items-center p-2 border-b">
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search..."
                  className="block w-full bg-gray-100 border-none rounded-sm py-2 pl-10 pr-3 text-sm placeholder-gray-500 focus:outline-none focus:bg-white"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
              <button onClick={() => setMobileSearchOpen(false)} className="p-2">
                <FiX className="h-6 w-6 text-gray-500" />
              </button>
            </div>
            <div className="absolute top-14 left-0 right-0 bottom-0 overflow-y-auto">
              <SearchResults
                isLoading={isLoading}
                results={searchResults}
                query={debouncedSearchQuery}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
